/**
 * Viewport anchoring for whole-page re-renders.
 *
 * Many activity pages rebuild their entire <main> after an action by calling
 * their `renderMain()` closure (a full `main.innerHTML = …` swap). That throws
 * the viewport around: the browser keeps the old scroll offset against brand-new
 * content, so the page appears to jump or scroll to the top, and any field the
 * player was typing in loses focus.
 *
 * `anchored(renderMain)` wraps such a re-render so the viewport stays put:
 *   • the window scroll position is captured before the rebuild and restored
 *     after (and re-asserted on the next frame, in case late layout — e.g.
 *     aspect-ratio images or an async sub-panel — shifts things);
 *   • the focused text field and its caret/selection are restored if that field
 *     still exists after the rebuild.
 *
 * It accepts a sync OR async render: if the render returns a promise, the
 * restore runs once it resolves. The very first render after a fresh navigation
 * is naturally at the top (the router clears the root, collapsing scroll to 0),
 * so anchoring it is a harmless no-op — no special-casing needed.
 *
 * Usage: replace post-action `renderMain();` calls with `anchored(renderMain);`.
 */

type RenderFn = () => void | Promise<void>;

/** Build a stable selector for the focused field so we can refocus it after the
 *  rebuild. Only text-entry fields are tracked — refocusing a button or link
 *  after it's been replaced would be surprising. Returns null when we can't
 *  identify the field reliably. */
function focusSelector(el: Element | null): string | null {
  if (!el) return null;
  const tag = el.tagName.toLowerCase();
  if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') return null;
  if (el.id) return `#${CSS.escape(el.id)}`;
  const name = el.getAttribute('name');
  if (name) return `${tag}[name="${CSS.escape(name)}"]`;
  for (const attr of ['data-field', 'data-input', 'data-amount', 'data-slug', 'data-recipe', 'data-key']) {
    const v = el.getAttribute(attr);
    if (v) return `${tag}[${attr}="${CSS.escape(v)}"]`;
  }
  return null;
}

export interface AnchorOptions {
  /** Force the viewport to the TOP after the render instead of preserving the
   *  current scroll. Use for status pages whose natural view is the top (e.g.
   *  the Roads countdown auto-refresh), where preserving a stale offset reads
   *  as the page drifting downward. */
  top?: boolean;
}

export function anchored(render: RenderFn, opts: AnchorOptions = {}): void {
  const scroller = document.scrollingElement ?? document.documentElement;
  const y = opts.top ? 0 : scroller.scrollTop;
  const x = opts.top ? 0 : scroller.scrollLeft;

  // Snapshot the focused text field + caret so typing isn't interrupted.
  const active = document.activeElement;
  const sel = focusSelector(active);
  let selStart: number | null = null;
  let selEnd: number | null = null;
  if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) {
    try {
      selStart = active.selectionStart;
      selEnd = active.selectionEnd;
    } catch {
      // selection isn't supported on some input types (number, email, …) — ignore
    }
  }

  const restore = (): void => {
    scroller.scrollTop = y;
    scroller.scrollLeft = x;
  };

  const restoreFocus = (): void => {
    if (!sel) return;
    const el = document.querySelector<HTMLElement>(sel);
    if (!el) return;
    el.focus({ preventScroll: true });
    if (selStart != null && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
      try {
        el.setSelectionRange(selStart, selEnd ?? selStart);
      } catch {
        // unsupported input type — caret restore is best-effort
      }
    }
  };

  const finish = (): void => {
    restore();
    restoreFocus();
    // Re-assert scroll after layout settles (late images, async sub-mounts).
    requestAnimationFrame(restore);
  };

  const result = render();
  if (result && typeof (result as Promise<void>).then === 'function') {
    void (result as Promise<void>).then(finish, finish);
  } else {
    finish();
  }
}
